import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useRobots } from '../../api/hooks';
import { useDoors } from '../../api/hooks/useDoors';
import { useLifts } from '../../api/hooks/useLifts';
import type { PlaceRegistry } from '../../api/v1';
import { DoorDetailDrawer } from '../Devices/DoorDetailDrawer';
import { LiftDetailDrawer } from '../Devices/LiftDetailDrawer';
import { useSiteLiftCars } from '../Devices/useSiteLiftCars';
import { RobotDetailDrawer } from '../Fleet/RobotDetailDrawer';
import { TaskDispatchForm } from '../Tasks/TaskDispatchForm';
import { TaskModal } from '../Tasks/TaskModal';
import { useTaskDispatchGate } from '../Tasks/useTaskDispatchGate';

/**
 * The four callbacks any map renderer emits, and nothing else.
 *
 * This is deliberately the exact prop shape `BuildingMapCanvas` already had.
 * A renderer's whole contract to the rest of the app is "the user pointed at
 * this thing"; what happens next is not its business.
 */
export interface MapSelectionHandlers {
  onSelectDoor: (name: string) => void;
  onSelectLift: (name: string) => void;
  onSelectRobot: (name: string) => void;
  onSelectPlace: (place: PlaceRegistry) => void;
}

/**
 * What the host has open: the kinds a renderer can hand it, keyed the way
 * the matching callback names them (a robot by its bare name, a zone by
 * its place name).
 */
export type MapSelectionKind = 'robot' | 'lift' | 'door' | 'zone';

export interface MapSelectionSubject {
  kind: MapSelectionKind;
  id: string;
}

/**
 * The render prop's SECOND argument (twin slice 1, W4 step 9; frozen in
 * C0's contract note §7), so a renderer can ring what is open and a page
 * can close it -- a landing for a different subject closes the open
 * drawer (W4 step 10). The four handlers stay the renderer's only way to
 * SELECT anything: this argument reads and closes, it never opens.
 */
export interface MapSelectionState {
  /** The subject whose drawer or form is open, or null. Null too while a
   *  selected robot, lift or door is missing from its list, because then
   *  no drawer is on screen to ring. */
  selected: MapSelectionSubject | null;
  /** Close whatever is open. Stable across renders. */
  clear(): void;
}

export interface MapSelectionHostProps {
  /** The renderer, given the four callbacks and what is open. A render
   *  prop rather than a child element so the host owns the handlers and
   *  the renderer cannot substitute its own. */
  children: (handlers: MapSelectionHandlers, selection: MapSelectionState) => ReactNode;
}

/** What is open: one subject at a time. A zone keeps its whole place,
 *  which the dispatch form is prefilled from. */
type OpenSubject =
  | { kind: 'robot' | 'lift' | 'door'; id: string }
  | { kind: 'zone'; id: string; place: PlaceRegistry };

/**
 * One selection model, one command surface, for every map renderer.
 *
 * WHY THIS EXISTS
 * The twin is a second way of looking at the same building. The failure mode
 * that matters is not that it looks wrong — it is that it becomes a second
 * way of COMMANDING the building, drifting from the first. This platform has
 * already paid for that class of bug twice: an adapter restart leaving a
 * projection key "active and uncancellable" while the cancel was answered
 * with silence and reported as accepted, and a `fire_relocalize` press with
 * nothing held returning a silent no-op that still reported success. Both
 * were second-path bugs.
 *
 * So the 2D canvas and the 3D twin are two renderers behind one host. Neither
 * screen owns a command; both hand the same four callbacks to the same
 * drawers and the same dispatch modal. A test asserts the two render an
 * identical set of mutation-capable components, which is the property that
 * actually matters — an import-path rule would pass by construction, since
 * every command already lives outside both screens.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 * It does not strip the drawers' write panels when the caller is the twin.
 * `LiftDetailDrawer` renders Advance, Recover, End session, a
 * call-to-destination confirm and the site-wide arm panel; `DoorDetailDrawer`
 * renders the door write gate and the override panel. Reusing the drawers
 * means the twin inherits all of it — and that is the accepted position,
 * because the identical click already reaches those panels from `/overview`
 * and `/devices`. The twin grants a signed-in operator no capability they do
 * not already have; exposure is bounded by the route's own role gate, not by
 * a second drawer behaviour that would have to be kept correct forever.
 */
export function MapSelectionHost({ children }: MapSelectionHostProps) {
  const robots = useRobots();
  const doorsQuery = useDoors();
  const liftsQuery = useLifts();

  // ONE subject at a time, which is what `selected` reports. A second
  // selection replaces the first rather than stacking a second drawer;
  // with a mouse the open drawer's outside-press dismiss has already
  // closed it by then (`closeIfOpen` has the order).
  const [open, setOpen] = useState<OpenSubject | null>(null);

  const doorView =
    open?.kind === 'door'
      ? ((doorsQuery.data ?? []).find((d) => d.name === open.id) ?? null)
      : null;
  const liftView =
    open?.kind === 'lift'
      ? ((liftsQuery.data ?? []).find((l) => l.name === open.id) ?? null)
      : null;
  const robotView =
    open?.kind === 'robot' ? ((robots.data ?? []).find((r) => r.name === open.id) ?? null) : null;
  const selectedZone = open?.kind === 'zone' ? open.place : null;
  // The site's cars with their building facts, for the lift drawer's Guests
  // and Robots lines. Read only once a lift is opened: a page that never
  // opens one never fetches the building model for it.
  const liftCars = useSiteLiftCars(open?.kind === 'lift');

  /**
   * A drawer closes ITSELF only. A close can land after the selection
   * has moved on -- the dispatch form's `onSuccess` resolves after its
   * POST, by when another subject may be open -- and must not shut the
   * subject that replaced it.
   *
   * The event order, for whatever builds on this (re-tap to clear): both
   * canvases SELECT on pointer-up (`BuildingMapCanvas`'s `onPointerUp`,
   * the twin's `up` gesture), and a drawer's outside-press dismiss
   * listens for `mousedown` (`useDismissOnOutside`). With a mouse that
   * is pointerdown, mousedown (the open drawer dismisses itself), then
   * pointerup (the next subject is selected), so a re-tap on the open
   * subject closes it on mousedown and opens it again on pointerup. On
   * touch the select comes first and the compatibility mousedown after
   * it; whether the next drawer's listener (attached a tick after it
   * mounts) is there to hear it is not established.
   */
  const closeIfOpen = useCallback((kind: MapSelectionKind, id: string) => {
    setOpen((cur) => (cur && cur.kind === kind && cur.id === id ? null : cur));
  }, []);
  const clear = useCallback(() => setOpen(null), []);

  const shownKind = doorView || liftView || robotView || selectedZone ? open?.kind : undefined;
  const shownId = shownKind ? open?.id : undefined;
  const selection = useMemo<MapSelectionState>(
    () => ({
      selected: shownKind && shownId !== undefined ? { kind: shownKind, id: shownId } : null,
      clear,
    }),
    [shownKind, shownId, clear],
  );

  /**
   * RE-TAP TO CLEAR (twin slice 1, plan W2 step 13): tapping the subject
   * whose drawer is open closes it, in both renderers.
   *
   * Not "selected === hit, so clear" at the moment of the select: with a
   * mouse the drawer's own outside-press dismiss has already closed it by
   * pointer-up (the order above), so that test would find nothing open
   * and open it again. The comparison is with what was open when the
   * PRESS began: a capturing `pointerdown` on the document notes it before
   * any drawer or renderer hears the press, and a bubbling `pointerup`
   * forgets it after the renderer's own pointer-up has selected (both
   * canvases select there). A select made during that press, of the
   * subject open at its start, closes it -- after a mouse's dismiss, or
   * before a touch's compatibility mousedown, the same answer.
   *
   * A select outside a press -- the callout's "Open <name> panel" is a
   * click, after its pointer-up -- is never a re-tap, and opens as it says.
   */
  const shownRef = useRef<MapSelectionSubject | null>(null);
  shownRef.current = selection.selected;
  const pressRef = useRef<{ openAtPress: MapSelectionSubject | null } | null>(null);
  useEffect(() => {
    const onPress = () => {
      pressRef.current = { openAtPress: shownRef.current };
    };
    const onRelease = () => {
      pressRef.current = null;
    };
    document.addEventListener('pointerdown', onPress, true);
    document.addEventListener('pointerup', onRelease);
    document.addEventListener('pointercancel', onRelease);
    return () => {
      document.removeEventListener('pointerdown', onPress, true);
      document.removeEventListener('pointerup', onRelease);
      document.removeEventListener('pointercancel', onRelease);
    };
  }, []);
  /** Open `next`, or close it when this press began on it open. */
  const select = (next: OpenSubject) => {
    const was = pressRef.current?.openAtPress;
    if (was && was.kind === next.kind && was.id === next.id) {
      closeIfOpen(next.kind, next.id);
      return;
    }
    setOpen(next);
  };

  // The same gate the Tasks screen uses — feature flag, backend readiness,
  // and role — so "may I dispatch?" gets one answer across every surface that
  // can dispatch.
  const dispatchGate = useTaskDispatchGate();

  const handlers: MapSelectionHandlers = {
    onSelectDoor: (name) => select({ kind: 'door', id: name }),
    onSelectLift: (name) => select({ kind: 'lift', id: name }),
    onSelectRobot: (name) => select({ kind: 'robot', id: name }),
    onSelectPlace: (place) => {
      // The markers are drawn for everyone — where the cleaning zones are is
      // read-only information, useful even to a viewer who cannot dispatch.
      // The CLICK is what the gate governs, so a denied viewer gets an inert
      // marker rather than a form that could only end in a 403.
      if (dispatchGate.writeDisabled) return;
      if (!place.is_cleaning_zone) return;
      select({ kind: 'zone', id: place.name, place });
    },
  };

  return (
    <>
      {children(handlers, selection)}

      {doorView && (
        <DoorDetailDrawer door={doorView} onClose={() => closeIfOpen('door', doorView.name)} />
      )}
      {liftView && (
        <LiftDetailDrawer
          // Keyed on the lift so a re-selection without an unmount does not
          // carry one lift's in-flight verb state to another
          // (useOperatorRequest.ts).
          key={liftView.name}
          lift={liftView}
          onClose={() => closeIfOpen('lift', liftView.name)}
          cars={liftCars}
        />
      )}
      {robotView && (
        <RobotDetailDrawer robot={robotView} onClose={() => closeIfOpen('robot', robotView.name)} />
      )}
      {selectedZone && (
        // Same shell and same form the Tasks calendar's create flow uses. The
        // form is self-sufficient about its own prefill — it re-asserts the
        // category once the task catalog arrives, and only accepts a zone the
        // catalog actually offers — so there is nothing to guard here beyond
        // handing it the two values.
        <TaskModal
          open
          // The form opens on Vacuum, the default mode; Sweep, Dust-mop and Scrub
          // are a pick away in its task-type list.
          title={`Vacuum ${selectedZone.name}`}
          cancelLabel="Cancel"
          onCancel={() => closeIfOpen('zone', selectedZone.name)}
        >
          <TaskDispatchForm
            prefillCategory="clean"
            prefillZone={selectedZone.name}
            onSuccess={() => closeIfOpen('zone', selectedZone.name)}
          />
        </TaskModal>
      )}
    </>
  );
}
